// src/components/commons/ThreatSearchPanel/ThreatSearchPanel.tsx
'use client'

import React, { useState, useEffect, useRef } from 'react'
import { useRouter } from 'next/navigation'
import NumberedHeader from '@/src/components/commons/generics/NumberedHeader/NumberedHeader'
import Image from 'next/image'
import { HorizontalSelectProps, CustomInputSearchProps } from './ThreatSearchPanel.interface'
import { FilterState, FilterOption } from '@/app/threat-library/ThreatLibrary.interface'
import { DROPDOWN_OPTIONS, useThreatFilters } from '@/app/threat-library/useThreatFilters'
import GenericCTA from '@/src/components/commons/generics/GenericCTA/GenericCTA'
import styles from './ThreatSearchPanel.module.scss'

import InputChevronDown from '@/public/assets/images/icon_input_angle_down_red.svg'
import FilterIcon from '@/public/assets/images/icon_filter_outline_red.svg'

// =========================================================================
// 1. HORIZONTAL MULTI-SELECT COMPONENT (Tag Display & Dropdown Logic)
// =========================================================================

const DEFAULT_ALL_VALUE = ''

const HorizontalMultiSelect: React.FC<HorizontalSelectProps> = ({
  label,
  options,
  selected,
  onChange,
  filterKey,
  isOpen,
  toggleDropdown,
}) => {
  const isProduct = filterKey === 'product'
  const defaultDisplayLabel = isProduct ? 'Both' : 'ALL'
  const defaultApiValue =
    options.find((opt) => opt.label === defaultDisplayLabel)?.value ?? DEFAULT_ALL_VALUE

  const handleToggleClick = (value: string) => {
    let newValues: string[]
    const isDefaultValue = value === defaultApiValue
    const currentIsDefault = selected.length === 1 && selected.includes(defaultApiValue)

    if (isDefaultValue) {
      newValues = [defaultApiValue]
    } else if (currentIsDefault) {
      newValues = [value]
    } else if (selected.includes(value)) {
      newValues = selected.filter((v: string) => v !== value)
    } else {
      newValues = [...selected, value]
    }

    if (newValues.length === 0) {
      newValues = [defaultApiValue]
    }

    onChange(newValues)
  }

  const handleRemoveTag = (e: React.MouseEvent<HTMLButtonElement>, valueToRemove: string) => {
    e.stopPropagation()

    let newValues = selected.filter((v) => v !== valueToRemove)

    if (newValues.length === 0) {
      newValues = [defaultApiValue]
    }

    onChange(newValues)
  }

  const handleDropdownToggleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter' && e.key !== ' ') {
      return
    }
    e.preventDefault()
    toggleDropdown()
  }

  const isDefaultSelected = selected.length === 1 && selected[0] === defaultApiValue
  const defaultText = defaultDisplayLabel

  return (
    <div className={styles.multiSelectGroup}>
      <span className={styles.fieldLabel}>{label}</span>

      <div className={styles.dropdownFieldSet}>
        <div
          className={`${styles.dropdownToggle} ${styles.tagContainer}`}
          role="button"
          tabIndex={0}
          onClick={toggleDropdown}
          onKeyDown={handleDropdownToggleKeyDown}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-label={`${label} filter options`}
        >
          {isDefaultSelected ? (
            <span className={`${styles.selectedTag} ${styles.defaultTag}`}>
              <span className={styles.tagLabel}>{defaultText}</span>
            </span>
          ) : (
            options
              .filter((opt) => selected.includes(opt.value))
              .map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={styles.selectedTag}
                  aria-label={`Remove filter: ${opt.label}`}
                  onClick={(e) => handleRemoveTag(e, opt.value)}
                >
                  <span className={styles.closeIcon} aria-hidden="true">
                    &times;
                  </span>
                  <span className={styles.tagLabel}>{opt.label}</span>
                </button>
              ))
          )}

          <button
            type="button"
            className={styles.toggleIcon}
            onClick={(e) => {
              e.stopPropagation()
              toggleDropdown()
            }}
            aria-hidden="true"
            tabIndex={-1}
          >
            <Image
              src={InputChevronDown.src}
              alt=""
              width={10}
              height={15}
              style={{
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
                transition: 'transform 0.2s',
              }}
            />
          </button>
        </div>

        <div className={`${styles.optionsContainer} ${isOpen ? styles.open : ''}`}>
          {options.map((opt: FilterOption) => (
            <button
              key={opt.value}
              type="button"
              className={
                // Add a class for non-selectable headers for specific styling
                `${opt.isSubOption ? '!pl-[24px]' : ''} ` +
                `${opt.isHeader ? styles.optionHeader : ''} ` +
                (selected.includes(opt.value) ? styles.optionSelected : styles.optionDefault)
              }
              onClick={() => handleToggleClick(opt.value)}
              disabled={opt.isHeader}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

// =========================================================================
// CUSTOM INPUT COMPONENT (ADAPTED FOR KEYWORD SEARCH BAR)
// =========================================================================
const CustomInputSearch: React.FC<CustomInputSearchProps> = ({ value, onChange, placeholder }) => (
  <div className={styles.keywordInputWrapper}>
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`${styles.keywordInput}`}
    />
  </div>
)

// =========================================================================
// 2. MAIN THREAT SEARCH PANEL COMPONENT
// =========================================================================
const ThreatSearchPanel: React.FC = () => {
  const router = useRouter()

  const EMPTY_DATA = { results: [] }
  const { filters, updateFilter, resetFilters } = useThreatFilters(EMPTY_DATA.results)

  const searchAreaRef = useRef<HTMLDivElement>(null)
  const [activeDropdownKey, setActiveDropdownKey] = useState<keyof FilterState | null>(null)
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(true)

  const toggleDropdown = (key: keyof FilterState) => {
    setActiveDropdownKey((prevKey) => (prevKey === key ? null : key))
  }

  // Closes dropdown when clicking outside the panel
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        activeDropdownKey !== null &&
        searchAreaRef.current &&
        !searchAreaRef.current.contains(event.target as Node)
      ) {
        setActiveDropdownKey(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [activeDropdownKey])

  // Handler for the text input (keyword search)
  const handleKeywordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateFilter('threatName', e.target.value)
  }

  const toggleMobileAccordion = () => {
    setIsFiltersCollapsed((prev) => !prev)
  }

  // --- SUBMIT LOGIC ---
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const params = new URLSearchParams()

    // Iterate over current filter state to build URL query parameters
    for (const [key, value] of Object.entries(filters)) {
      if (Array.isArray(value)) {
        const activeValues = value.filter((v) => v !== 'ALL' && v !== 'Both' && v !== '')
        if (activeValues.length > 0) {
          params.append(key, activeValues.join(','))
        }
      } else if (typeof value === 'boolean') {
        // Handle boolean fields explicitly
        if (value === true) {
          params.append(key, 'true') // Only append if the boolean is true
        }
      } else if (value) {
        params.append(key, value)
      }
    }

    // Route to the Threat Library page with query params for pre-filtering
    router.push(`/threat-library?${params.toString()}`)
  }

  const txtNumber = '03'
  const txtTitle = 'Threat Library'
  const txtSubtitle = 'Look for threat information'
  const txtSearch = 'Search'
  const txtShowFilters = 'Show filters'
  const txtHidefilters = 'Hide filters'
  const txtFindThreat = 'FIND THREAT'
  const txtClear = 'CLEAR'

  // Helper render function for the primary CTAs
  const renderCtaButtons = (
    <div className={styles.searchActionGroup}>
      {/* Submit Button */}
      <GenericCTA
        ctaText={txtFindThreat}
        ctaURL="#" // Set to # or empty string to make it non-navigational
        onClick={handleSubmit} // Attach the form submit logic to onClick
        variant="red"
        className={styles.submitButton}
      />

      {/* Clear Form Button */}
      <GenericCTA
        ctaText={txtClear}
        ctaURL="#" // Non-navigational link
        onClick={resetFilters}
        variant="dark-gray"
        className={styles.clearButton}
        iconSrc={FilterIcon.src}
        iconAlt="Clear Icon"
        iconWidth={12}
        iconHeight={12}
      />
    </div>
  )

  return (
    <div className={`${styles.threatSearchPanel} w-full`}>
      <div className={`${styles.container} mx-auto`}>
        <div
          className={`${styles.titleContainer} mb-10 relative flex justify-between items-center flex-col`}
        >
          <NumberedHeader
            number={txtNumber}
            title={txtTitle}
            subtitle={txtSubtitle}
            variant="red"
            className="mx-auto mb-0!"
          />
        </div>

        <form onSubmit={handleSubmit} className={styles.searchPanelForm}>
          <div className={styles.formFields}>
            <div
              ref={searchAreaRef}
              className={`${styles.filtersContainer} ${!isFiltersCollapsed ? styles.filtersContainerOpen : ''}`}
            >
              {/* DROPDOWNS CONTAINER (2x2 / 4x1 grid based on breakpoint) */}

              <button
                type="button"
                className={`${styles.mobileFiltersAccordionToggle} md:hidden!`}
                onClick={toggleMobileAccordion}
              >
                {!isFiltersCollapsed ? txtHidefilters : txtShowFilters}
                <Image
                  src={InputChevronDown.src}
                  alt="Dropdown Toggle"
                  width={10}
                  height={15}
                  style={{
                    transform: !isFiltersCollapsed ? 'rotate(180deg)' : 'rotate(0)',
                    transition: 'transform 0.2s',
                  }}
                  className={styles.filtersAccordionToggleArrow}
                />
              </button>
              <div
                className={`${styles.filterAccordionWrapper} ${!isFiltersCollapsed ? styles.filterAccordionOpen : ''}`}
              >
                {/* Category */}
                <HorizontalMultiSelect
                  label="CATEGORY"
                  filterKey="category"
                  options={DROPDOWN_OPTIONS.category}
                  selected={filters.category}
                  onChange={(v: string[]) => updateFilter('category', v)}
                  isOpen={activeDropdownKey === 'category'}
                  toggleDropdown={() => toggleDropdown('category')}
                />

                {/* Threat Severity */}
                <HorizontalMultiSelect
                  label="THREAT SEVERITY"
                  filterKey="severity"
                  options={DROPDOWN_OPTIONS.severity}
                  selected={filters.severity}
                  onChange={(v: string[]) => updateFilter('severity', v)}
                  isOpen={activeDropdownKey === 'severity'}
                  toggleDropdown={() => toggleDropdown('severity')}
                />

                {/* Direction */}
                <HorizontalMultiSelect
                  label="DIRECTION"
                  filterKey="direction"
                  options={DROPDOWN_OPTIONS.direction}
                  selected={filters.direction}
                  onChange={(v: string[]) => updateFilter('direction', v)}
                  isOpen={activeDropdownKey === 'direction'}
                  toggleDropdown={() => toggleDropdown('direction')}
                />

                {/* Engines */}
                <HorizontalMultiSelect
                  label="ENGINES"
                  filterKey="engines"
                  options={DROPDOWN_OPTIONS.engines}
                  selected={filters.engines}
                  onChange={(v: string[]) => updateFilter('engines', v)}
                  isOpen={activeDropdownKey === 'engines'}
                  toggleDropdown={() => toggleDropdown('engines')}
                />
              </div>
            </div>
            {/* SEARCH INPUT ROW (Keyword Input + Tablet/Desktop CTAs) */}
            <div className={styles.searchActionContainer}>
              <div className={styles.inputFieldSet}>
                <span className={styles.fieldLabel}>{txtSearch}</span>
                {/* Keyword Input */}
                <CustomInputSearch
                  value={filters.threatName}
                  onChange={handleKeywordChange}
                  placeholder="Keyword search"
                />
              </div>

              {/* Desktop/Tablet CTAs (Hidden on mobile) */}
              <div className={`${styles.desktopCtaGroup} hidden md:flex`}>{renderCtaButtons}</div>
            </div>
          </div>
          {/* MOBILE-ONLY CTAs */}
          <div className={`${styles.mobileCTAsContainer} md:hidden`}>
            <div className={styles.mobileCtaGroup}>{renderCtaButtons}</div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ThreatSearchPanel
